/**
 * Civil dates in Europe/Dublin (T853 M2).
 *
 * ⛔ WHY THIS EXISTS AT ALL. The invoice window — which month, and what counts as
 * "already travelled" — must be decided by ONE clock, on the server. The screen
 * used to build it from the browser's `new Date()`, so the same button pressed
 * on two machines in different offsets could produce two different documents
 * from the same data. `journey_date` is a DATE with no timezone, so once the
 * boundaries are resolved to civil Dublin dates every comparison downstream is
 * timezone-free.
 *
 * ⛔ THE LOCALE IS 'en-CA' AND THAT IS NOT COSMETIC. It is the one common locale
 * whose numeric format is already ISO order (YYYY-MM-DD), so the parts can be
 * read straight off. `en-IE` — the obvious guess for an Irish business — yields
 * DD/MM/YYYY and would silently produce a nonsense key.
 *
 * ⚠️ There is no other server-side Dublin date helper in this repo:
 * `Europe/Dublin` appears elsewhere only as `Intl` FORMATTING options for
 * human-facing strings. This is the first one that computes a key.
 */

const DUBLIN = 'Europe/Dublin';

const CIVIL_DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: DUBLIN,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * The civil date in Dublin at `instant`, as ISO 'YYYY-MM-DD'.
 *
 * ⚠️ NOT `instant.toISOString().slice(0, 10)`, which is the UTC date and is a
 * DIFFERENT DAY for roughly one hour of every Irish summer day. MEASURED: at
 * 2026-08-31T23:30:00Z the UTC date is 2026-08-31 while Dublin has already
 * turned 2026-09-01 — so a naive implementation bills an August invoice under a
 * September window, or vice versa.
 */
export function dublinCivilDate(instant: Date): string {
  return CIVIL_DATE_FMT.format(instant);
}

/**
 * ⭐ Board 2814 M1 (plan DD-4, DD-11) - HAS THIS INSTANT'S DUBLIN DAY ALREADY
 * PASSED AT `now`? The ONE rule for "past" on a nursing request, read by the
 * crossing route (which refuses a past request) and by the Requests screen
 * (which offers no Confirm on one), so the two cannot disagree about which day
 * a late-evening request belongs to.
 * ⛔ FALSE FOR ANYTHING UNPARSEABLE, AND FOR NULL: a request with no date (a
 * repeating one) is never past, so a missing date can never refuse a booking.
 * ⚠️ BOTH SIDES GO THROUGH `dublinCivilDate`, never a UTC slice: today's day is
 * the Dublin day at `now`, which is what makes 23:30 on the same evening "today".
 */
export function isBeforeDublinToday(iso: string | null | undefined, now: Date): boolean {
  if (typeof iso !== 'string' || iso.trim() === '') return false;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return false;
  return dublinCivilDate(at) < dublinCivilDate(now);
}

const DUBLIN_WALL = new Intl.DateTimeFormat('en-GB', {
  timeZone: DUBLIN,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** Dublin's offset from UTC at `ms`, in minutes (60 in IST, 0 in GMT). */
function dublinOffsetMinutesAt(ms: number): number {
  const p: Record<string, number> = {};
  for (const part of DUBLIN_WALL.formatToParts(ms)) p[part.type] = Number(part.value);
  const hour = p['hour'] === 24 ? 0 : p['hour']!;
  const wall = Date.UTC(p['year']!, p['month']! - 1, p['day']!, hour, p['minute']!, p['second']!);
  return (wall - Math.floor(ms / 1000) * 1000) / 60_000;
}

/**
 * ⭐ board 2814 M5 (plan DD-9) - THE INSTANT A DUBLIN WALL CLOCK NAMES, as ISO, or
 * null: the phoned-in trip's return time (`bookings.return_at`, an instant) from the
 * trip's day and the `HH:MM` Helen typed.
 * ⛔ A PORT OF `portal/src/shared/dublin-time.ts`'s `dublinInstant`, with its
 * policy: strict `YYYY-MM-DD` and 24-hour `HH:MM`, a day that does not survive a
 * calendar round trip is refused, a REPEATED time (the October change) means the
 * earlier one, and a MISSING time (the March change) moves forward. The kiosk does
 * not import the portal's tree, so the rule lives in both; edit both.
 */
export function dublinInstant(day: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) return null;
  const y = Number(day.slice(0, 4));
  const m = Number(day.slice(5, 7));
  const d = Number(day.slice(8, 10));
  const wall = Date.UTC(y, m - 1, d, Number(time.slice(0, 2)), Number(time.slice(3, 5)));
  const check = new Date(wall);
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) return null;
  const DAY_MS = 86_400_000;
  const before = dublinOffsetMinutesAt(wall - DAY_MS);
  const agreeing = [...new Set([before, dublinOffsetMinutesAt(wall + DAY_MS)])]
    .map((off) => ({ off, ms: wall - off * 60_000 }))
    .filter(({ off, ms }) => dublinOffsetMinutesAt(ms) === off)
    .map(({ ms }) => ms);
  return new Date(agreeing.length > 0 ? Math.min(...agreeing) : wall - before * 60_000).toISOString();
}

/**
 * ⭐⭐ T1717 M6 (DD5, P0-7) - ONE RENDERING OF A RETURN INSTANT, FOR EVERY
 * SURFACE THAT SHOWS ONE. "Tue, 1 Sept, 18:45" in Europe/Dublin, or null.
 *
 * ⛔ ONE FUNCTION, FOUR READERS, AND THAT IS THE WHOLE REASON IT IS HERE rather
 * than inlined at each site. The return time now appears on Helen's portal
 * request row, on the booking detail, on the Quote screen's "From the customer"
 * card and in the driver/team WhatsApp message. Four copies of an
 * `Intl.DateTimeFormat` call is four chances for one of them to drift onto the
 * host locale or the host timezone, and the whole point of pinning the zone is
 * that a kiosk, a container and a reviewer's laptop compose the SAME string for
 * the same booking. `src/shared` is the one directory both the browser bundle
 * and the server-side notify layer import from.
 *
 * ⚠️ THE ZONE IS PINNED AND SO IS THE LOCALE. `en-IE` fixes day-before-month;
 * ⛔ do NOT copy `dublinCivilDate`'s `en-CA` here - that locale exists above
 * because it computes a KEY in ISO order, and it would print an American-looking
 * date at an Irish operator.
 *
 * ⚠️ THE OUTPUT CONTAINS COMMAS AND NO DASHES, deliberately. `check-copy.sh`
 * bans em and en dashes across `src/client`, `src/shared` and `src/notify`, and
 * this string is composed into copy in all three.
 *
 * ⛔ NULL FOR ANYTHING UNPARSEABLE, NEVER "Invalid Date". Every caller treats
 * null as "omit the line", which is the honest answer: no time is invented, and
 * a garbled instant cannot reach a driver as a date-shaped lie.
 */
export function formatDublinInstant(iso: string | null | undefined): string | null {
  if (typeof iso !== 'string' || iso.trim() === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IE', {
    timeZone: DUBLIN,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * ⭐⭐ T2088 M7 (plan D5; ADR-0006) — WHAT STANDS WHERE THE RETURN INSTANT WOULD,
 * when the home said the time is not known yet.
 *
 * ⛔ THE WORDS ARE THE PORTAL'S, COPIED ON PURPOSE, AND THEY MAY NOT DRIFT.
 * `portal/src/client/copy.ts`'s `returnToConfirm` is `'time to confirm'`, and
 * the nurse has already read it on her own two lists before Helen ever sees the
 * row. One job must read the same in both buildings — that is the same property
 * `jobRef` exists for — and the two repositories share no module, so the only
 * thing holding them together is this note and the specs either side of the
 * seam. If one is ever reworded, reword the other in the same change.
 */
export const RETURN_TIME_TO_CONFIRM = 'time to confirm';

/**
 * ⭐ board 2871 (Captain's ruling) - what the booking detail's `Back` row says when
 * the job comes back but nobody has stated WHEN: no stored time, no portal marker
 * and none of the customer's own text. A returning job never loses its Back row;
 * an absent row read as "no return" while the price was doubled for one.
 */
export const RETURN_TIME_NOT_SET = 'time not set';

/**
 * ⭐⭐ T2088 M7 — THE RETURN LEG'S "WHEN", IN ONE PLACE, for the three kiosk
 * surfaces where Helen meets a DOUBLED figure: her Requests row, the crossed
 * booking and the Quote card.
 *
 * ⛔ IT IS NOT GATED ON `returnTrip` AND MUST NOT BE. Each caller holds a
 * DIFFERENT flag — the nurse's answer off the seam, the operator's own
 * `bookings.return_trip`, the job's live `options.returnTrip` — and folding any
 * one of them in here would make the other two read the wrong switch. The outer
 * gate stays at every call site; this only answers "what does the WHEN say".
 *
 * ⛔ THE MARKER OUTRANKS THE INSTANT, AND THE ORDER IS LOAD-BEARING. The portal
 * CHECK (`requests_to_confirm_needs_timeless_return`) refuses `'to-confirm'`
 * beside a known instant, so the two cannot both be set on a row that arrived
 * honestly — but if a future upstream build ever contradicted itself, saying
 * "time to confirm" is the answer that cannot mislead: it promises a call, where
 * printing the instant would state a time that something on the row disputes.
 *
 * ⛔ NULL MEANS OMIT THE LINE, exactly as `formatDublinInstant` does — a timeless
 * return whose state is `'unset'` (the recurring residual, and every row written
 * before portal v14) keeps the silence it has always had. It was never promised
 * a time, so there is nothing to confirm.
 */
export function formatReturnWhen(
  iso: string | null | undefined,
  returnAtState: string | null | undefined,
): string | null {
  if (returnAtState === 'to-confirm') return RETURN_TIME_TO_CONFIRM;
  return formatDublinInstant(iso);
}

const BACK_DATE = new Intl.DateTimeFormat('en-IE', {
  timeZone: DUBLIN,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

const BACK_CLOCK = new Intl.DateTimeFormat('en-IE', {
  timeZone: DUBLIN,
  hour: '2-digit',
  minute: '2-digit',
  // `hour12: false` alone renders midnight as 24:00 on some ICU builds.
  hourCycle: 'h23',
});

/**
 * ⭐ board 2871 (Captain's ruling, C5) - A RETURN INSTANT IN THE `When` ROW'S
 * SHAPE, for the two readers that sit beside a When: the booking detail's `Back`
 * row and the driver's "Back at" line. "Sat 10 Oct, 22:00", or with the year when
 * the return is not in the trip's year ("Sun 10 Oct 2027, 18:00"), or null.
 * ⛔ THE DATE PART IS `@ecmb/ui`'s `formatWhenParts` date, rebuilt here because
 * `src/shared` also feeds the server: en-IE parts in Europe/Dublin, the non-literal
 * ones joined by single spaces (no comma after the weekday). A spec pins the two
 * equal, so they cannot drift. Every other reader keeps `formatDublinInstant`.
 */
export function formatBackInstant(iso: string | null | undefined, outboundDay: string | null | undefined): string | null {
  if (typeof iso !== 'string' || iso.trim() === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = BACK_DATE.formatToParts(d).filter((p) => p.type !== 'literal').map((p) => p.value).join(' ');
  const year = returnYearDiffers(iso, outboundDay) ? ` ${dublinCivilDate(d).slice(0, 4)}` : '';
  return `${date}${year}, ${BACK_CLOCK.format(d)}`;
}

/** ⭐ board 2871 (C5) - `formatReturnWhen`'s rule in the Back row's shape: the
 *  home's to-confirm marker, else the instant (`formatBackInstant`), else null. */
export function formatBackWhen(
  iso: string | null | undefined,
  returnAtState: string | null | undefined,
  outboundDay: string | null | undefined,
): string | null {
  if (returnAtState === 'to-confirm') return RETURN_TIME_TO_CONFIRM;
  return formatBackInstant(iso, outboundDay);
}

/**
 * ⭐ board 2871 (Captain review, security Low 2) - true when the return's Dublin year
 * is not the outbound day's year, so the reader prints the year. A return typed one
 * year forward otherwise reads as the right day and month with the wrong weekday.
 * false when either side is missing or unreadable: no year is invented.
 */
export function returnYearDiffers(iso: string | null | undefined, outboundDay: string | null | undefined): boolean {
  if (typeof iso !== 'string' || typeof outboundDay !== 'string' || !/^\d{4}-/.test(outboundDay)) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return dublinCivilDate(d).slice(0, 4) !== outboundDay.slice(0, 4);
}

/**
 * The civil date a LOCAL-MIDNIGHT `Date` was CONSTRUCTED to mean, as ISO
 * 'YYYY-MM-DD' (T1011 M7).
 *
 * ⛔ THIS IS NOT `dublinCivilDate` AND THE TWO ARE NOT INTERCHANGEABLE — the
 * difference is a whole day and it lands on a money document, so it is written
 * out rather than left to a reader's instinct.
 *  · `dublinCivilDate` maps an INSTANT (a real moment: "now") onto the date it
 *    is in Dublin. That is the right question for "what is today" and "which
 *    month is this", which is why the invoice window uses it.
 *  · This maps a DATE-SHAPED `Date` — one built by `new Date(y, m, d)`, i.e.
 *    LOCAL midnight, carrying no instant anybody cares about — back onto the
 *    y/m/d it was built from. It is the exact inverse of that constructor, so
 *    it round-trips in EVERY process timezone.
 *
 * ⚠️ WHY BOTH TRAPS ARE REAL, MEASURED 2026-08-13 on `new Date(2026, 7, 4)` —
 * the pattern date "4 August 2026" as `datesForSchedule` builds it:
 *  · `toISOString().slice(0, 10)` is the UTC date. Under TZ=Europe/Dublin that
 *    instant is 2026-08-03T23:00:00Z, so it reads **2026-08-03** — one day
 *    early, and across a month boundary it moves the run into the previous
 *    month's invoice. This is the trap `dublinCivilDate`'s own note names.
 *  · `dublinCivilDate` of that same local-midnight `Date` is only correct in a
 *    process at or west of Dublin. Under TZ=Pacific/Auckland local midnight is
 *    2026-08-03T12:00:00Z, whose Dublin civil date is **2026-08-03** — one day
 *    early again. Passing a date-shaped `Date` to an instant formatter cannot be
 *    right, because there is no instant to format.
 * ⇒ Local getters, and only local getters. No `TZ` is set in the Dockerfile or
 * the stack scripts, so both traps are LATENT today and both go live the day the
 * process timezone moves.
 */
export function civilDateFromLocalMidnight(d: Date): string {
  return `${pad4(d.getFullYear())}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Normalise a DATE column value to ISO 'YYYY-MM-DD' whichever shape the driver
 * hands over (T1011 M7 — LIFTED VERBATIM out of `core/data/billables-repo.ts`,
 * which is now its first caller rather than its owner).
 *
 * ⛔ IT MOVED BECAUSE IT ACQUIRED A SECOND CALLER, NOT FOR TIDINESS. The
 * occurrence generator reads `bookings.journey_date` and `booking_series.
 * start_date` and has to compare them with the invoice's civil window; a second
 * copy of the rule below is exactly the drift that would put the generator and
 * the biller one day apart on the same run.
 *
 * ⛔ WHICH LANE PRODUCES WHICH SHAPE IS THE OPPOSITE OF THE OBVIOUS GUESS.
 * `core/db.ts` installs a GLOBAL `pg.types.setTypeParser(1082, v => v)`, so under
 * REAL POSTGRES a DATE arrives as a plain **string** — db.ts says why: node-pg's
 * default parses it into a local-midnight Date that JSON-serialises to a shifted
 * UTC timestamp. pg-mem does not see that override and yields a **Date at UTC
 * midnight**. So the string branch is production and the Date branch is the fast
 * lane.
 *
 * ⛔ THE DATE BRANCH MUST READ **UTC** PARTS — the opposite of
 * `civilDateFromLocalMidnight` above, because this `Date` was built by a driver
 * at UTC midnight rather than by `new Date(y, m, d)` at local midnight. MEASURED:
 * under TZ=America/New_York, local getters reported the €390 reproduction and the
 * month-boundary test one day early. Dublin and UTC hid it, so CI and this
 * machine were green while a US contributor would have seen three spurious reds
 * on a correct implementation.
 */
export function civilDateFromDbDate(v: unknown): string {
  if (v instanceof Date) {
    return `${pad4(v.getUTCFullYear())}-${pad2(v.getUTCMonth() + 1)}-${pad2(v.getUTCDate())}`;
  }
  return String(v).slice(0, 10);
}

export type MonthWindow = {
  /** 'YYYY-MM-DD', inclusive. */
  monthStart: string;
  /** 'YYYY-MM-DD', EXCLUSIVE — half-open, so no month-boundary double count. */
  monthEndExclusive: string;
  year: number;
  /** 1-12, NOT a 0-based index. */
  month: number;
};

/** ISO 'YYYY-MM' with a real month, e.g. '2026-08'. Rejects '2026-13'. */
const MONTH_KEY = /^(\d{4})-(0[1-9]|1[0-2])$/;

/**
 * Parse a 'YYYY-MM' key into a half-open civil window, or null if it is not a
 * real month or the year is implausible.
 *
 * The year bound is deliberately wide but finite: it exists so a junk or hostile
 * parameter cannot ask the database to scan a window no booking could occupy,
 * not to express a business rule.
 */
export function monthWindowFromKey(key: string, opts?: { minYear?: number; maxYear?: number }): MonthWindow | null {
  const m = MONTH_KEY.exec(key);
  if (m === null) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const minYear = opts?.minYear ?? 2000;
  const maxYear = opts?.maxYear ?? 2200;
  if (year < minYear || year > maxYear) return null;

  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  return {
    monthStart: `${pad4(year)}-${pad2(month)}-01`,
    monthEndExclusive: `${pad4(nextYear)}-${pad2(nextMonth)}-01`,
    year,
    month,
  };
}

/** The window containing "now" in Dublin — the default when no month is asked for. */
export function currentDublinMonthWindow(now: Date): MonthWindow {
  const today = dublinCivilDate(now);
  // Slicing the already-Dublin-resolved civil date, never re-reading the clock:
  // a second read could straddle midnight and disagree with `today`.
  const window = monthWindowFromKey(today.slice(0, 7));
  /* c8 ignore next -- dublinCivilDate always yields a real month, so this is unreachable */
  if (window === null) throw new Error('dublin month key did not parse');
  return window;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
function pad4(n: number): string {
  return String(n).padStart(4, '0');
}
